import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import { useSelector } from 'react-redux';
import { ChatBubble, ChatBubbleOutlineOutlined } from '@mui/icons-material';
import { IconButton, Typography } from '@mui/material';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import { toast } from 'react-toastify';
import Avatar from '../Layout/Avatar';
import FlexBetween from '../FlexBetween';
import Friend from '../Friend';
import WidgetWrapper from '../WidgetWrapper';
import VideoPlayer from '../Content/VideoPlayer';
import { server } from '../../server';
import { getStoredAuthToken } from '../../utils/authSession';

const FALLBACK_AVATAR = 'data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%2280%22 height=%2280%22 viewBox=%220 0 80 80%22%3E%3Crect width=%2280%22 height=%2280%22 rx=%2240%22 fill=%22%2323292f%22/%3E%3Ccircle cx=%2240%22 cy=%2231%22 r=%2214%22 fill=%22%236ee000%22/%3E%3Cpath d=%22M17 70c4-16 16-25 23-25s19 9 23 25%22 fill=%22%236ee000%22 opacity=%22.75%22/%3E%3C/svg%3E';

const getCurrentProfile = () => sessionStorage.getItem('currentProfile') || 'User';

const getAuthHeaders = () => {
  const token = getStoredAuthToken();
  const headers = { 'current-profile': getCurrentProfile() };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  return headers;
};

const getAvatarUrl = (profile) => {
  if (!profile?.avatar) {
    return FALLBACK_AVATAR;
  }

  return typeof profile.avatar === 'string' ? profile.avatar : profile.avatar.url || FALLBACK_AVATAR;
};

const getCommenter = (comment) => comment.commenter || comment.userId || comment.shopId || comment.user;

const RoomPostCard = ({ roomId, post }) => {
  const { user } = useSelector((state) => state.user);
  const { seller } = useSelector((state) => state.seller);
  const [isLiked, setIsLiked] = useState(Boolean(post.isLikedByCurrentUser));
  const [likeCount, setLikeCount] = useState(post.likeCount || 0);
  const [isCommentsOpen, setIsCommentsOpen] = useState(false);
  const [comments, setComments] = useState(Array.isArray(post.comments) ? post.comments : []);
  const [commentText, setCommentText] = useState('');
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);

  const currentProfile = getCurrentProfile();
  const isLoggedIn = Boolean(user?._id || seller?._id);
  const formattedDate = post.createdAt ? format(new Date(post.createdAt), 'PPP') : '';
  const owner = post.owner && typeof post.owner === 'object'
    ? post.owner
    : { _id: post.owner, name: post.name, avatar: post.avatar };
  const ownerAvatar = getAvatarUrl(owner) || post.avatar || FALLBACK_AVATAR;

  const totalComments = comments.length;

  useEffect(() => {
    setIsLiked(Boolean(post.isLikedByCurrentUser));
    setLikeCount(post.likeCount || 0);
    setComments(Array.isArray(post.comments) ? post.comments : []);
  }, [post]);

  const currentProfileId = useMemo(() => {
    if (currentProfile === 'User') {
      return user?._id;
    }

    return seller?._id;
  }, [currentProfile, seller?._id, user?._id]);

  const handleLike = async () => {
    if (!isLoggedIn) {
      toast.error('Please login to engage with this community.');
      return;
    }

    const wasLiked = isLiked;
    const previousCount = likeCount;

    setIsLiked(!wasLiked);
    setLikeCount(wasLiked ? Math.max(0, previousCount - 1) : previousCount + 1);

    try {
      const response = await axios.patch(
        `${server}/room/rooms/${roomId}/posts/${post._id}/like`,
        { currentProfile },
        {
          withCredentials: true,
          headers: getAuthHeaders(),
        }
      );

      setIsLiked(Boolean(response.data.liked));
      setLikeCount(response.data.likeCount || 0);
    } catch (error) {
      setIsLiked(wasLiked);
      setLikeCount(previousCount);
      toast.error(error.response?.data?.message || 'Failed to update like.');
    }
  };

  const handleCommentSubmit = async (event) => {
    event.preventDefault();

    if (!commentText.trim()) {
      return;
    }

    if (!isLoggedIn) {
      toast.error('Please login to comment in this community.');
      return;
    }

    setIsSubmittingComment(true);

    try {
      const response = await axios.post(
        `${server}/room/rooms/${roomId}/posts/${post._id}/comments`,
        { commentText, currentProfile },
        {
          withCredentials: true,
          headers: getAuthHeaders(),
        }
      );

      setComments((currentComments) => [...currentComments, response.data]);
      setCommentText('');
      setIsCommentsOpen(true);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to post comment.');
    } finally {
      setIsSubmittingComment(false);
    }
  };

  return (
    <WidgetWrapper m="2rem 0" className="post-widget-wrapper room-post-card">
      <Friend
        friendId={owner._id}
        friendType={post.profileType}
        name={owner.name || post.name}
        avatar={ownerAvatar}
        imageSize="55px"
      />
      {formattedDate && <div className="post-widget-date">{formattedDate}</div>}
      {post.description && <Typography sx={{ mt: '1rem' }}>{post.description}</Typography>}

      {post.link?.url && (
        <a href={post.link.url} target="_blank" rel="noopener noreferrer" className="link-post-preview-link">
          <div className="link-post-preview-container">
            {post.link.image && <img src={post.link.image} alt={post.link.title} className="link-post-preview-image" />}
            <div className="link-post-preview-content">
              <h3 className="link-post-preview-title">{post.link.title}</h3>
              <p className="link-post-preview-description">{post.link.description}</p>
              <p className="link-post-preview-url">{post.link.url}</p>
            </div>
          </div>
        </a>
      )}

      <div className="post-widget-media-container">
        {Array.isArray(post.images) && post.images.length > 0 && (
          <div className="post-widget-image-gallery">
            {post.images.map((image, index) => (
              <LazyLoadImage
                key={image._id || image.key || image.url || index}
                width="100%"
                height="auto"
                alt={`community post ${index + 1}`}
                className="post-widget-image"
                src={image.url}
              />
            ))}
          </div>
        )}
        {Array.isArray(post.videos) && post.videos.length > 0 && (
          <div className="post-widget-video-gallery">
            {post.videos.map((video, index) => (
              <div key={video._id || video.key || video.url || index} className="post-widget-video-container">
                {video.key ? (
                  <VideoPlayer videoUrl={video.key} />
                ) : (
                  <video controls className="post-widget-video">
                    <source src={video.url} type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <FlexBetween mt="0.25rem">
        <FlexBetween gap="1rem">
          <FlexBetween gap="0.3rem">
            <IconButton onClick={handleLike}>
              <img
                src={isLiked
                  ? 'https://imagedelivery.net/mEHe7vWpwEHOhne-YSqzMw/b0d6f7b5-d727-4f1d-cda5-a1cf207eae00/public'
                  : 'https://imagedelivery.net/mEHe7vWpwEHOhne-YSqzMw/1d169027-443a-4f85-8c7d-707000933f00/public'}
                alt={isLiked ? 'Liked' : 'Not liked'}
                className={isLiked ? 'post-widget-like-icon-liked' : 'post-widget-like-icon'}
              />
            </IconButton>
            <Typography>{likeCount}</Typography>
          </FlexBetween>

          <FlexBetween gap="0.3rem">
            <IconButton onClick={() => setIsCommentsOpen((isOpen) => !isOpen)}>
              {totalComments > 0 ? <ChatBubble className="post-widget-icons" /> : <ChatBubbleOutlineOutlined className="post-widget-icons" />}
            </IconButton>
            <Typography>{totalComments}</Typography>
          </FlexBetween>
        </FlexBetween>
      </FlexBetween>

      {isCommentsOpen && (
        <div className="room-post-comments-section">
          <div className="room-post-comments-list">
            {comments.map((comment) => {
              const commenter = getCommenter(comment);
              const commenterName = commenter?.name || 'Community member';
              const commenterAvatar = getAvatarUrl(commenter);

              return (
                <div key={comment._id} className="post-widget-comment-container room-post-comment-row">
                  <div className="post-widget-comment-avatar-container">
                    <img className="post-widget-comment-avatar" src={commenterAvatar} alt={commenterName} />
                  </div>
                  <div className="post-widget-comment-content">
                    <span className="post-widget-comment-author">{commenterName}</span>
                    <div className="post-widget-comment-box">
                      <span className="post-widget-comment-text">{comment.commentText || comment.comment}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <form className="room-post-comment-form" onSubmit={handleCommentSubmit}>
            <Avatar />
            <textarea
              value={commentText}
              onChange={(event) => setCommentText(event.target.value)}
              rows={3}
              className="room-post-comment-input"
              placeholder="Add your comment..."
            />
            <button type="submit" className="post-widget-comment-submit-button" disabled={isSubmittingComment}>
              {isSubmittingComment ? 'POSTING...' : 'POST'}
            </button>
          </form>
        </div>
      )}
    </WidgetWrapper>
  );
};

export default RoomPostCard;